// frontend/src/components/PlayersPicksPage.jsx
import React, { useEffect, useMemo, useState } from "react";

/**
 * Adds a hard cutoff at Thu 1:00 PM CT:
 * - Shows banner on the login page with open/closed status
 * - Blocks verification once closed (no entry to picks UI)
 * - On submit, surfaces server 403/409 window-closed messages
 * Keeps existing UI/behavior exactly as-is: 10-pick requirement, buttons, messages.
 */

const REQUIRED_PICKS = 10;
const TZ = "America/Chicago";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:5001"
    : "https://pickem-backend-2025.onrender.com");

const SECTION = {
  LOGIN: "LOGIN",
  PICKS: "PICKS",
  READONLY: "READONLY",
  DONE: "DONE",
};

// ---------- helpers ----------
const norm = (s) => String(s || "").trim().toLowerCase();

async function fetchJSON(url, init) {
  const r = await fetch(url, { cache: "no-store", ...(init || {}) });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

/** CST "now" as a Date. */
function cstNow() {
  const s = new Date().toLocaleString("en-US", { timeZone: TZ });
  return new Date(s);
}

/** Returns the Date for this week's Thursday 1:00 PM CT (relative to "now"). */
function thursday1pmThisWeekCST(nowCst = cstNow()) {
  const d = new Date(nowCst);
  // 0=Sun..6=Sat; Thursday = 4
  const offset = 4 - d.getDay();
  d.setDate(d.getDate() + offset);
  d.setHours(13, 0, 0, 0); // 1:00 PM
  return d;
}

/** Validate name + pin with server if possible; otherwise fall back to roster.json */
async function validateGameNameAndPin(gameName, pin) {
  const name = (gameName || "").trim();
  const pinStr = String(pin || "").trim();

  if (!name || !pinStr) {
    return { ok: false, reason: "empty" };
  }

  // Try server auth first (if implemented)
  try {
    const r = await fetch(`${API_BASE}/api/authenticate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        gameName: name,
        pin: pinStr,
      }),
    });

    if (r.ok) {
      const j = await r.json();

      // any { ok:true } or { success:true } shape
      if (j && (j.ok || j.success)) {
        return {
          ok: true,
          canon: j.canonicalName || name,
        };
      }

      if (j?.reason === "name") {
        return {
          ok: false,
          reason: "name",
        };
      }

      if (j?.reason === "pin") {
        return {
          ok: false,
          reason: "pin",
          canon: j.canonicalName || name,
        };
      }

      return {
        ok: false,
        reason: "auth",
      };
    }
  } catch (_) {
    /* fall through */
  }

  // Fallback: read roster.json and validate locally
  try {
    const rosterRes =
      (await fetch(`${API_BASE}/api/roster`).catch(() => null)) ||
      (await fetch(`${API_BASE}/data/roster.json`).catch(() => null));

    if (!rosterRes || !rosterRes.ok) {
      return {
        ok: false,
        reason: "name",
      };
    }

    const roster = await rosterRes.json();

    const rows = Array.isArray(roster)
      ? roster
      : Object.values(roster);

    const rec = rows.find(
      (r) =>
        norm(r?.gameName || r?.player || r?.name || r?.Name) ===
        norm(name)
    );

    if (!rec) {
      return {
        ok: false,
        reason: "name",
      };
    }

    const rosterPin = String(
      rec?.pin || rec?.PIN || rec?.Pin || ""
    ).trim();

    if (!rosterPin || rosterPin !== pinStr) {
      return {
        ok: false,
        reason: "pin",
        canon:
          rec.gameName ||
          rec.player ||
          rec.name ||
          name,
      };
    }

    return {
      ok: true,
      canon:
        rec.gameName ||
        rec.player ||
        rec.name ||
        name,
    };
  } catch {
    return {
      ok: false,
      reason: "name",
    };
  }
}

// ---------- component ----------
export default function PlayersPicksPage() {
  const [section, setSection] = useState(SECTION.LOGIN);
  const [activated, setActivated] = useState(false);

  const [currentWeek, setCurrentWeek] = useState(null);
  const [games, setGames] = useState([]);

  const [playerName, setPlayerName] = useState("");
  const [pin, setPin] = useState("");

  const [picks, setPicks] = useState([]);
  const selectedCount = useMemo(() => picks.length, [picks]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [nameError, setNameError] = useState("");
  const [pinError, setPinError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  // Cutoff logic (CT)
  const [windowClosed, setWindowClosed] = useState(false);
  const [cutoffText, setCutoffText] = useState("");

  useEffect(() => {
    setActivated(true);
  }, []);

  // current week
  useEffect(() => {
    (async () => {
      try {
        const wkObj = await fetchJSON(
          `${API_BASE}/data/current_week.json`
        );

        const wk =
          typeof wkObj === "number"
            ? wkObj
            : wkObj?.currentWeek ??
              wkObj?.week ??
              wkObj?.current_week ??
              1;

        setCurrentWeek(wk || 1);
      } catch {
        setCurrentWeek(1);
        setNotice(
          "⚠️ Could not load current week; defaulting to Week 1."
        );
      }
    })();
  }, []);

  // compute/update cutoff status whenever "now" ticks or week changes
  useEffect(() => {
    function updateCutoff() {
      const now = cstNow();
      const cutoff = thursday1pmThisWeekCST(now);
      const closed = now >= cutoff;

      setWindowClosed(closed);
      setCutoffText("Thursday 1:00 PM CT");
    }

    updateCutoff();

    // keep it fresh with a minute tick; inexpensive
    const t = setInterval(updateCutoff, 60000);

    return () => clearInterval(t);
  }, [currentWeek]);

  // games
  useEffect(() => {
    if (!currentWeek) return;

    (async () => {
      setLoading(true);
      setError("");

      try {
        const data = await fetchJSON(
          `${API_BASE}/data/games_week_${currentWeek}.json`
        );

        if (!Array.isArray(data)) {
          throw new Error("bad games");
        }

        const normalized = data.map((g, i) => ({
          index: i,
          date: g.date || "",
          team1: g.team1,
          spread1: g.spread1,
          team2: g.team2,
          spread2: g.spread2,
        }));

        setGames(normalized);
      } catch {
        setGames([]);
        setError(
          `No games available for Week ${currentWeek}.`
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [currentWeek]);

  // toggle (with hard stop at 10)
  function togglePick(gameIndex, team) {
    setError("");
    setNotice("");

    setPicks((prev) => {
      const existing = prev.find(
        (p) => p.gameIndex === gameIndex
      );

      if (existing?.pick === team) {
        return prev.filter(
          (p) => p.gameIndex !== gameIndex
        );
      }

      if (existing && existing.pick !== team) {
        return prev.map((p) =>
          p.gameIndex === gameIndex
            ? { gameIndex, pick: team }
            : p
        );
      }

      if (prev.length >= REQUIRED_PICKS) {
        setError(
          "You already have 10 picks — deselect one to choose a different game."
        );
        return prev;
      }

      return [
        ...prev,
        {
          gameIndex,
          pick: team,
        },
      ];
    });
  }

  // verify login
  async function handleVerify(e) {
    e?.preventDefault?.();

    setNameError("");
    setPinError("");
    setError("");
    setNotice("");

    // Hard gate: do not allow entering the picker UI after cutoff
    if (windowClosed) {
      setError(
        `⛔ The pick window for Week ${currentWeek} closed at ${cutoffText}. Picks are no longer accepted.`
      );
      setSection(SECTION.LOGIN);
      return;
    }

    setIsVerifying(true);

    const res = await validateGameNameAndPin(
      playerName,
      pin
    );

    setIsVerifying(false);

    if (!res.ok) {
      if (res.reason === "name") {
        setNameError(
          "Please check the spelling of your Game Name."
        );
      } else if (res.reason === "pin") {
        setPinError(
          "Your PIN number is invalid."
        );
      } else {
        setError(
          "Unable to verify Game Name / PIN right now."
        );
      }

      setSection(SECTION.LOGIN);
      return;
    }

    if (res.canon && res.canon !== playerName) {
      setPlayerName(res.canon);
    }

    // Already picked?
    try {
      const r = await fetch(
        `${API_BASE}/api/check-player-picks`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            week: currentWeek,
            playerName:
              res.canon || playerName,
          }),
        }
      );

      if (r.ok) {
        const j = await r.json();

        if (j?.alreadyPicked) {
          setSection(SECTION.READONLY);
          setNotice(
            "You already submitted picks for this week. Picks are final."
          );

          if (Array.isArray(j.picks)) {
            setPicks(j.picks);
          }

          return;
        }
      }
    } catch {
      /* ignore; still allow making picks (if open) */
    }

    setSection(SECTION.PICKS);

    setNotice(
      `You’re verified. Make your selections below. The window closes ${cutoffText}.`
    );
  }

  // submit
  async function handleSubmit() {
    setError("");
    setNotice("");

    if (selectedCount !== REQUIRED_PICKS) {
      if (selectedCount < REQUIRED_PICKS) {
        setError(
          `You need to have 10 picks before submitting. You currently have ${selectedCount}.`
        );
      } else {
        setError(
          `You’ve selected ${selectedCount}. Deselect ${
            selectedCount - REQUIRED_PICKS
          } to submit.`
        );
      }

      return;
    }

    // final re-check name/pin before POST
    const res = await validateGameNameAndPin(
      playerName,
      pin
    );

    if (!res.ok) {
      if (res.reason === "name") {
        setError(
          "Please check the spelling of your Game Name."
        );
      } else if (res.reason === "pin") {
        setError(
          "Your PIN number is invalid."
        );
      } else {
        setError(
          "Unable to verify Game Name / PIN right now."
        );
      }

      return;
    }

    const picksSorted = [...picks].sort(
      (a, b) => a.gameIndex - b.gameIndex
    );

    try {
      const r = await fetch(
        `${API_BASE}/api/submit-picks`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            week: currentWeek,
            player:
              res.canon || playerName.trim(),
            pin: String(pin || "").trim(),
            picks: picksSorted,
          }),
        }
      );

      // Friendly handling for window-closed from server
      if (!r.ok) {
        let msg =
          "Submission failed. Please try again.";

        try {
          const j = await r.json();

          if (j?.message) {
            msg = j.message;
          }
        } catch {}

        if (
          r.status === 403 ||
          r.status === 409
        ) {
          msg =
            `⛔ The pick window for Week ${currentWeek} ` +
            `closed at ${cutoffText}. Your picks were not saved.`;
        }

        setError(msg);
        return;
      }

      const j = await r.json();

      if (j?.success) {
        setSubmitSuccess(true);
        setSection(SECTION.DONE);

        setNotice(
          "✅ Your picks have been submitted. Picks will be visible to all players after the Thursday 1:00pm CST lockout."
        );
      } else if (
        String(j?.message || "")
          .toLowerCase()
          .includes("already")
      ) {
        setSection(SECTION.READONLY);

        setNotice(
          "It looks like you already submitted picks for this week. Picks are final."
        );
      } else {
        setError(
          j?.message ||
            "Submission failed. Please try again."
        );
      }
    } catch (e) {
      setError(
        String(
          e?.message ||
            "Submission failed. Please try again."
        )
      );
    }
  }

  // counter UI
  const StickyCounter = () => (
    <div
      style={{
        position: "sticky",
        top: 64,
        zIndex: 10,
        background: "#f9f9f9",
        padding: 10,
        borderBottom: "1px solid #ddd",
      }}
    >
      <p
        style={{
          margin: 0,
          fontWeight: "bold",
          fontSize: 16,
          color:
            selectedCount === REQUIRED_PICKS
              ? "green"
              : "red",
        }}
      >
        You have selected {selectedCount} out of{" "}
        {REQUIRED_PICKS} games.
      </p>

      <p
        style={{
          margin: 0,
          fontStyle: "italic",
          fontSize: 14,
          color: "#555",
        }}
      >
        Picks will be viewable after Thursday at
        1:00pm CST. Submission is final!
      </p>
    </div>
  );

  if (!activated) return null;

  const submitHelper =
    selectedCount === REQUIRED_PICKS
      ? ""
      : selectedCount < REQUIRED_PICKS
      ? `Select ${
          REQUIRED_PICKS - selectedCount
        } more`
      : `Deselect ${
          selectedCount - REQUIRED_PICKS
        }`;

  // small banner on the login page about the window status
  const WindowBanner =
    section === SECTION.LOGIN ? (
      <div
        style={{
          marginBottom: 10,
          padding: "10px 12px",
          borderRadius: 8,
          border:
            "1px solid " +
            (windowClosed
              ? "#c62828"
              : "#1976d2"),
          background: windowClosed
            ? "#fdecea"
            : "#e3f2fd",
          color: windowClosed
            ? "#b71c1c"
            : "#0d47a1",
          fontWeight: 600,
        }}
      >
        {windowClosed
          ? `The pick window for Week ${currentWeek} closed at ${cutoffText}. Picks are no longer accepted.`
          : `Pick window for Week ${currentWeek} closes ${cutoffText}. You must submit 10 picks. Submissions are final.`}
      </div>
    ) : null;

  return (
    <div
      className="page-container"
      style={{ padding: 12 }}
    >
      {submitSuccess ? (
        <>
          <h2>
            ✅ Your picks have been submitted.
          </h2>

          <p>
            Picks will be visible after the
            Thursday 1:00pm CST lockout. Good
            luck!
          </p>
        </>
      ) : section !== SECTION.PICKS &&
        section !== SECTION.READONLY ? (
        // LOGIN
        <>
          <h2>Place Your Picks</h2>

          {WindowBanner}

          {notice && (
            <div
              style={{
                marginBottom: 10,
                color: "#8a6d00",
                fontWeight: 600,
              }}
            >
              {notice}
            </div>
          )}

          {error && (
            <div
              style={{
                marginBottom: 10,
                color: "#b00020",
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}

          <form
            onSubmit={handleVerify}
            style={{
              maxWidth: 520,
              padding: 12,
              border: "1px solid #ddd",
              borderRadius: 12,
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              Player verification
            </h3>

            <label
              style={{
                display: "block",
                fontWeight: 600,
                marginTop: 8,
              }}
            >
              Game Name
            </label>

            <input
              type="text"
              value={playerName}
              onChange={(e) =>
                setPlayerName(e.target.value)
              }
              placeholder="Exact Game Name from roster"
              autoComplete="off"
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 8,
                border: "1px solid #ccc",
              }}
              disabled={windowClosed}
            />

            <div
              style={{
                marginTop: 6,
                fontSize: 13,
                color: "#555",
                lineHeight: 1.4,
              }}
            >
              <strong>
                Not sure of your exact Game Name?
              </strong>{" "}
              Open the Roster page, copy your Game
              Name, then paste it here.
            </div>

            {nameError && (
              <div
                style={{
                  color: "#b00020",
                  marginTop: 6,
                }}
              >
                {nameError}
              </div>
            )}

            <label
              style={{
                display: "block",
                fontWeight: 600,
                marginTop: 12,
              }}
            >
              PIN
            </label>

            <input
              type="password"
              value={pin}
              onChange={(e) =>
                setPin(e.target.value)
              }
              placeholder="Your private PIN"
              autoComplete="off"
              style={{
                width: "100%",
                padding: "8px 10px",
                borderRadius: 8,
                border: "1px solid #ccc",
              }}
              disabled={windowClosed}
            />

            {pinError && (
              <div
                style={{
                  color: "#b00020",
                  marginTop: 6,
                }}
              >
                {pinError}
              </div>
            )}

            <button
              type="submit"
              disabled={
                isVerifying || windowClosed
              }
              style={{
                marginTop: 14,
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #888",
                background: "#fafafa",
              }}
              title={
                windowClosed
                  ? "Window closed"
                  : "Verify your Game Name and PIN"
              }
            >
              {isVerifying
                ? "Checking…"
                : windowClosed
                ? "Window closed"
                : "Verify"}
            </button>
          </form>
        </>
      ) : (
        // PICKS / READONLY
        <>
          <h2>Place Your Picks</h2>

          {notice && (
            <div
              style={{
                marginBottom: 10,
                color: "#8a6d00",
                fontWeight: 600,
              }}
            >
              {notice}
            </div>
          )}

          {error && (
            <div
              style={{
                marginBottom: 10,
                color: "#b00020",
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}

          {section === SECTION.PICKS && (
            <StickyCounter />
          )}

          {loading && <p>Loading games...</p>}

          {!loading &&
            games.length === 0 && (
              <p>No games available.</p>
            )}

          {!loading &&
            games.length > 0 && (
              <>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginTop: 10,
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: "left",
                        }}
                      >
                        Date/Time
                      </th>

                      <th
                        style={{
                          textAlign: "left",
                        }}
                      >
                        Team 1
                      </th>

                      <th></th>

                      <th
                        style={{
                          textAlign: "left",
                        }}
                      >
                        Team 2
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {games.map((g) => {
                      const selected =
                        picks.find(
                          (p) =>
                            p.gameIndex ===
                            g.index
                        )?.pick;

                      return (
                        <tr key={g.index}>
                          <td>{g.date}</td>

                          <td>
                            <button
                              onClick={() =>
                                togglePick(
                                  g.index,
                                  g.team1
                                )
                              }
                              disabled={
                                section ===
                                SECTION.READONLY
                              }
                              style={{
                                backgroundColor:
                                  selected ===
                                  g.team1
                                    ? "#d0f0c0"
                                    : "",
                                fontWeight:
                                  selected ===
                                  g.team1
                                    ? "bold"
                                    : "normal",
                                padding:
                                  "6px 10px",
                                margin: 4,
                                cursor:
                                  section ===
                                  SECTION.READONLY
                                    ? "not-allowed"
                                    : "pointer",
                              }}
                            >
                              {g.team1} (
                              {g.spread1 > 0
                                ? "+"
                                : ""}
                              {g.spread1})
                            </button>
                          </td>

                          <td
                            style={{
                              fontWeight:
                                "bold",
                            }}
                          >
                            vs
                          </td>

                          <td>
                            <button
                              onClick={() =>
                                togglePick(
                                  g.index,
                                  g.team2
                                )
                              }
                              disabled={
                                section ===
                                SECTION.READONLY
                              }
                              style={{
                                backgroundColor:
                                  selected ===
                                  g.team2
                                    ? "#d0f0c0"
                                    : "",
                                fontWeight:
                                  selected ===
                                  g.team2
                                    ? "bold"
                                    : "normal",
                                padding:
                                  "6px 10px",
                                margin: 4,
                                cursor:
                                  section ===
                                  SECTION.READONLY
                                    ? "not-allowed"
                                    : "pointer",
                              }}
                            >
                              {g.team2} (
                              {g.spread2 > 0
                                ? "+"
                                : ""}
                              {g.spread2})
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {section ===
                  SECTION.PICKS && (
                  <>
                    <div
                      style={{
                        margin: "12px 0",
                        padding: 12,
                        border:
                          "1px dashed #999",
                      }}
                    >
                      <strong>
                        Reminder:
                      </strong>{" "}
                      Double-check your picks.
                      Submission is final for
                      the week. Everyone’s picks
                      are shown after the
                      Thursday 1:00pm CST
                      lockout.
                    </div>

                    {submitHelper && (
                      <div
                        style={{
                          marginBottom: 8,
                          color: "#b00020",
                          fontWeight: 600,
                        }}
                      >
                        {submitHelper}
                      </div>
                    )}

                    <button
                      onClick={handleSubmit}
                      disabled={
                        selectedCount !==
                        REQUIRED_PICKS
                      }
                      title={
                        selectedCount !==
                        REQUIRED_PICKS
                          ? "You must have exactly 10 picks to submit"
                          : "Submit your final picks"
                      }
                      style={{
                        backgroundColor:
                          selectedCount ===
                          REQUIRED_PICKS
                            ? "#4CAF50"
                            : "#9e9e9e",
                        color: "white",
                        border: "none",
                        borderRadius: 4,
                        cursor:
                          selectedCount ===
                          REQUIRED_PICKS
                            ? "pointer"
                            : "not-allowed",
                        padding:
                          "10px 16px",
                      }}
                    >
                      Submit Final Picks
                    </button>
                  </>
                )}

                {section ===
                  SECTION.READONLY && (
                  <div
                    style={{
                      marginTop: 8,
                      fontStyle: "italic",
                    }}
                  >
                    Your picks are locked for
                    Week {currentWeek}. Thanks!
                  </div>
                )}
              </>
            )}
        </>
      )}
    </div>
  );
}